import React from "react";
import PageBanner from "../components/PageBanner";
import CTASection from "../components/home/CTASection";
import ProcessSection from "../components/home/ProcessSection";
import useReveal from "../hooks/useReveal";
import { SERVICES, IMAGES } from "../mock";
import { Check, ArrowRight } from "lucide-react";
import { useEstimate } from "../components/EstimateContext";
import Seo from "../components/Seo";

export default function Services() {
  useReveal();
  const { openEstimate } = useEstimate();
  const residential = SERVICES.filter((s) => s.category === "residential");
  const commercial = SERVICES.filter((s) => s.category === "commercial");

  const Card = ({ s, i }) => (
    <div className={`reveal delay-${(i % 3) + 1} group bg-white rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-2xl transition-all duration-300`}>
      <div className="relative h-60 overflow-hidden">
        <img src={s.image} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="p-7">
        <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-2 group-hover:text-[#2f9fe6] transition-colors">{s.title}</h3>
        <p className="text-[#6b7683] text-sm leading-relaxed mb-5">{s.short}</p>
        <ul className="grid grid-cols-2 gap-2 mb-6">
          {s.points.map((p) => (
            <li key={p} className="flex items-center gap-2 text-sm text-[#4a5560]">
              <Check className="w-4 h-4 text-[#2f9fe6] shrink-0" /> {p}
            </li>
          ))}
        </ul>
        <button onClick={openEstimate} className="group/btn inline-flex items-center gap-2 text-[#2f9fe6] font-semibold hover:gap-3 transition-all">
          Request this service <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Seo title="Services" path="/services" description="Kitchen, bathroom, basement, custom interior and commercial renovation services in Edmonton by Classic Interior Renovations." />
      <PageBanner
        crumb="Services"
        title="Home renovations built to last"
        subtitle="From kitchens and bathrooms to basements, custom interiors and commercial fit-outs — we do it all."
        image={IMAGES.kitchen3}
      />

      <section id="residential" className="py-24 bg-white scroll-mt-24">
        <div className="container-x">
          <div className="max-w-2xl mb-14 reveal">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Residential</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight">Residential renovation services</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {residential.map((s, i) => <Card key={s.slug} s={s} i={i} />)}
          </div>
        </div>
      </section>

      <ProcessSection />

      <section id="commercial" className="py-24 bg-[#f7f9fb] scroll-mt-24">
        <div className="container-x">
          <div className="max-w-2xl mb-14 reveal">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Commercial</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight">Commercial construction & renovation</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {commercial.map((s, i) => <Card key={s.slug} s={s} i={i} />)}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
